import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import { isBefore, isSameDay, subDays } from "date-fns";
import { escapeRegExp } from "lodash";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import { Dispatch } from "redux";
import tinyColor from "tinycolor2";

import { MoreHoriz, OneColored, Spy } from "@livechat/design-system-icons";
import {
  ActionMenu,
  ActionMenuItem,
  Button,
  Text as DSText,
  DesignToken,
  IActionMenuOption,
  Icon,
  SpacingToken,
  Tag,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import {
  authorIsHuman,
  eventContainsTicketFeedMessage,
  eventSupportsDeliveryStatus,
  getEventDate,
  getFormattedEventDate,
  getPlainTextMessageText,
  messageIsEmpty,
  messageIsPlainText,
} from "helpers/tickets";
import {
  Author,
  DetailedSource,
  EventType,
  IAttachment,
  IEvent,
  IEventAuthor,
  ITicketDeliveryStatus,
  ITicketEventSource,
} from "interfaces/entities/ticket";
import { IStoreState } from "interfaces/storeState";
import { IAgent, selectLoggedInAgent } from "store/entities/agents";
import { selectIsDarkMode } from "store/features/app";
import { TicketsViewActions } from "store/views/tickets/actions";
import { getIsLoadingOriginalMessage, selectEventById } from "store/views/tickets/selectors";
import { textEllipsisStyles } from "styles/common";
import { wordWrap } from "styles/wordWrap";
import { withVar } from "themes/helpers";

import { MessageUnstrippedContent } from "./MessageUnstrippedContent";
import { ShowUnstrippedContentButton } from "./ShowUnstrippedContentButton";
import { TicketAttachments } from "./TicketAttachments";
import { TicketDeliveryStatus } from "./TicketDeliveryStatus";
import { MessageElementWrapper } from "./styles";

interface IProps {
  id: string;
  author: IEventAuthor | null;
  agents: IAgent[];
  isPrivate: boolean;
  source?: ITicketEventSource;
  type: EventType;
  testId: string;
  date: Date;
  message: React.ReactElement;
  attachments: IAttachment[] | null;
  isMobile?: boolean;
  deliveryStatuses?: ITicketDeliveryStatus[];
  ticketId: string;
  stripped?: boolean;
  replySuggestionIDs?: string[];
}

function getBackground(author: IEventAuthor | null, isPrivate: boolean): string {
  if (author == null) {
    return withVar(DesignToken.SurfaceInvertSecondary);
  }

  if (author.type === Author.Customer) {
    return withVar(DesignToken.SurfaceTertiaryDefault);
  }

  if (isPrivate) {
    return withVar(DesignToken.SurfaceInvertSecondary);
  }

  return withVar(DesignToken.SurfaceAccentEmphasisHighInfo);
}

function getFontStyle(author: IEventAuthor | null): string {
  if (author && !authorIsHuman(author.type)) {
    return "italic";
  }
  return "normal";
}

function getColor(author: IEventAuthor | null, isPrivate: boolean): string {
  if (!author) {
    return withVar(DesignToken.ContentInvertPrimary);
  }

  if (author.type === Author.Customer) {
    return withVar(DesignToken.ContentBasicPrimary);
  }

  if (author.type === Author.Rule || (!isPrivate && author.type === Author.Agent)) {
    return withVar(DesignToken.ContentLockedWhite);
  }

  return withVar(DesignToken.ContentInvertPrimary);
}

interface IWrapper {
  isMobile?: boolean;
}

const Wrapper = styled.div<IWrapper>`
  margin: ${(p): string => (p.isMobile ? "20px" : "40px 60px")};
`;

const metaCss = css`
  display: flex;
  align-items: center;
  gap: var(${SpacingToken.Spacing2});
  flex-direction: row;
  justify-content: space-between;
  font-size: 14px;
  line-height: 1.43em;
  min-height: 1.43em;
`;

interface IMetaTop {
  author: IEventAuthor | null;
  type: EventType;
  isPrivate: boolean;
}

const StyledMetaTop = styled.div<IMetaTop>`
  ${metaCss};
  background: ${(p): string => getBackground(p.author, p.isPrivate)};
  color: ${(p): string => getColor(p.author, p.isPrivate)};
  padding: 12px;
  border-radius: 8px 8px 0 0;
`;

const StyledMetaBottom = styled.div`
  ${metaCss};
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: ${withVar(SpacingToken.Spacing3)};
  margin-top: ${withVar(SpacingToken.Spacing05)};
`;

const StyledAuthor = styled(Text)`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
`;

const StyledIcon = styled(Icon)`
  margin-right: 6px;
`;

const StyledPrivateMessageInfo = styled.span`
  margin-left: 4px;
  font-weight: normal;
`;

const StyledBubble = styled.div<{
  author: IEventAuthor | null;
  type: EventType;
  isPrivate: boolean;
}>`
  border-radius: 8px;
  font-size: 14px;
  font-style: ${({ author }): string => getFontStyle(author)};
  line-height: 1.33;
  padding: 12px;
  border: 1px solid var(${DesignToken.BorderBasicSecondary});
  border-top-width: 0;
  border-radius: 0 0 8px 8px;

  ${wordWrap};

  color: var(${DesignToken.ContentBasicPrimary});

  a {
    color: var(${DesignToken.ActionPrimaryDefault});
    text-decoration: underline;
  }

  a[href=""] {
    pointer-events: none;
    appearance: none;
    text-decoration: none;
    color: inherit;
  }

  ol {
    list-style: decimal;
    margin: 10px 20px;
  }

  ul {
    list-style: disc;
    margin: 10px 10px 10px 25px;
  }

  code {
    font-size: 14px;
    white-space: pre-wrap;
  }

  li {
    margin: initial;
    padding: initial;
    line-height: 1.4;
  }

  h1 {
    font-size: 32px;
    line-height: 40px;
  }

  h2 {
    font-size: 24px;
    line-height: 28px;
  }

  h3 {
    font-size: 20px;
    line-height: 24px;
  }

  h4 {
    font-size: 16px;
    line-height: 24px;
  }

  h5 {
    font-size: 15px;
    line-height: 22px;
  }

  h6 {
    font-size: 12px;
    line-height: 20px;
  }

  blockquote {
    padding: 0.5em 1em;
    background-color: ${withVar(DesignToken.SurfaceSecondaryDefault)};
    margin: 0.5em 0;
  }

  img {
    max-width: 100%;
    height: auto;
    image-orientation: from-image;
  }

  img.hd-image {
    max-width: 500px;
    height: auto;
  }

  .mention {
    ${(p): SerializedStyles =>
      p.isPrivate
        ? css`
            font-weight: 600;
          `
        : css``}
  }

  @media (max-width: 500px) {
    img.hd-image {
      max-width: 250px;
      height: auto;
    }
  }
`;

const EmptyMessage = styled.span`
  color: var(${DesignToken.ContentBasicPrimary});
  font-style: italic;
`;

function getAuthorName(agents: IAgent[], author: IEventAuthor | null): string | null {
  if (!author) {
    return null;
  }

  if (author.type === Author.System || author.type === Author.Rule) {
    return "Automated message";
  }

  if (author.type === Author.Customer) {
    return author.name || author.email || null;
  }

  if (author.type === Author.Agent) {
    const agentId = author.id;
    const agentNameFallback = author.name;
    const agent = agents.find((agentItem: IAgent) => agentItem.id === agentId);
    return (agent ? agent.name : agentNameFallback) ?? null;
  }

  return null;
}

function getAuthorEmail(agents: IAgent[], author: IEventAuthor | null): string | null {
  if (!author) return null;

  if (author.type === Author.Customer) {
    return author.email ?? null;
  }

  if (author.type === Author.Agent) {
    const agentId = author.id;
    const agent = agents.find((agentItem: IAgent) => agentItem.id === agentId);
    return agent ? agent.email : null;
  }

  return null;
}

const adjustInlineStyleColorsToDarkMode = (template: string, backgroundColor = "#202024"): string => {
  const adjustTextColor = (backgroundColor: string, textColor: string): string => {
    if (tinyColor.isReadable(backgroundColor, textColor)) return textColor;

    let lightenColor = tinyColor(textColor);
    let lightenValue = 10;

    do {
      lightenColor = lightenColor.lighten(lightenValue);
      lightenValue += 10;
    } while (!tinyColor.isReadable(backgroundColor, lightenColor));

    return lightenColor.toHexString();
  };

  const replaceColorInTemplate = (template: string, originalColor: string | RegExp, newColor: string): string => {
    const colorRegex = new RegExp(
      typeof originalColor === "string" ? escapeRegExp(originalColor) : originalColor,
      "gi"
    );
    return template.replace(colorRegex, newColor);
  };

  const hexColorRegex = /#(?:[0-9a-fA-F]{3}){1,2}/gi;
  const rgbAndRgbaColorRegex =
    /(rgb(a)?\(\s*(?!\d+(?:\.|\s*-?)\d+\.\d+)-?(?:\d*\.\d+|\d+)(%?)(?:(?:\s*,\s*-?(?:\d+|\d*\.\d+)){2}(?:\s*,\s*-?(?:\d+|\d*\.\d+)%?)?|(?:(?:\s*-?\d*\.\d+|\s*-\d+|\s+\d+){2}|(?:\s*-?(?:\d+|\d*\.\d+)%){2})(?:\s*\/\s*-?(?:\d+|\d*\.\d+)%?)?)\s*\))/gi;
  const hslAndHslaColorRegex =
    /^hsla?\(\s*(?<h>[-+]?\d{1,3}(?:\.\d+)?)(deg|grad|rad|turn)?\s*(?:,\s*|\s+)\s*(?<s>[-+]?\d{1,3}(?:\.\d+)?)%\s*(?:,\s*|\s+)\s*(?<l>[-+]?\d{1,3}(?:\.\d+)?)%\s*(?:,\s*|\s+)?(?:\s*\/?\s*(?<alpha>[-+]?[\d.]+%?)\s*)?\)$/gi;
  const namedColorRegex =
    /AliceBlue|AntiqueWhite|Aqua|Aquamarine|Azure|Beige|Bisque|Black|BlanchedAlmond|Blue|BlueViolet|Brown|BurlyWood|CadetBlue|Chartreuse|Chocolate|Coral|CornflowerBlue|Cornsilk|Crimson|Cyan|DarkBlue|DarkCyan|DarkGoldenRod|DarkGray|DarkGrey|DarkGreen|DarkKhaki|DarkMagenta|DarkOliveGreen|DarkOrange|DarkOrchid|DarkRed|DarkSalmon|DarkSeaGreen|DarkSlateBlue|DarkSlateGray|DarkSlateGrey|DarkTurquoise|DarkViolet|DeepPink|DeepSkyBlue|DimGray|DimGrey|DodgerBlue|FireBrick|FloralWhite|ForestGreen|Fuchsia|Gainsboro|GhostWhite|Gold|GoldenRod|Gray|Grey|Green|GreenYellow|HoneyDew|HotPink|IndianRed|Indigo|Ivory|Khaki|Lavender|LavenderBlush|LawnGreen|LemonChiffon|LightBlue|LightCoral|LightCyan|LightGoldenRodYellow|LightGray|LightGrey|LightGreen|LightPink|LightSalmon|LightSeaGreen|LightSkyBlue|LightSlateGray|LightSlateGrey|LightSteelBlue|LightYellow|Lime|LimeGreen|Linen|Magenta|Maroon|MediumAquaMarine|MediumBlue|MediumOrchid|MediumPurple|MediumSeaGreen|MediumSlateBlue|MediumSpringGreen|MediumTurquoise|MediumVioletRed|MidnightBlue|MintCream|MistyRose|Moccasin|NavajoWhite|Navy|OldLace|Olive|OliveDrab|Orange|OrangeRed|Orchid|PaleGoldenRod|PaleGreen|PaleTurquoise|PaleVioletRed|PapayaWhip|PeachPuff|Peru|Pink|Plum|PowderBlue|Purple|RebeccaPurple|Red|RosyBrown|RoyalBlue|SaddleBrown|Salmon|SandyBrown|SeaGreen|SeaShell|Sienna|Silver|SkyBlue|SlateBlue|SlateGray|SlateGrey|Snow|SpringGreen|SteelBlue|Tan|Teal|Thistle|Tomato|Turquoise|Violet|Wheat|White|WhiteSmoke|Yellow|YellowGreen/gi;
  const colorRegex = new RegExp(
    `(${hexColorRegex.source}|${rgbAndRgbaColorRegex.source}|${hslAndHslaColorRegex.source}|${namedColorRegex.source})`,
    "gi"
  );
  const inlineStyleRegex = /style=("|')(.*?)("|')/gi;
  const backgroundColorRegex = /(background|background-color):\s*/gi;
  const inlineStylesInTemplate = template.match(inlineStyleRegex) || [];
  const inlineStyleColors = new Set<string>(inlineStylesInTemplate.join().match(colorRegex) || []);
  const mixBlendModeStyle = "mix-blend-mode: screen";

  let adjustedTemplate = template;

  // Adjust text colors in inline styles
  inlineStyleColors.forEach(color => {
    const adjustedColor = adjustTextColor(backgroundColor, color);
    adjustedTemplate = replaceColorInTemplate(adjustedTemplate, color, adjustedColor);
  });

  // Change background colors in inline styles to app background color
  adjustedTemplate = replaceColorInTemplate(
    adjustedTemplate,
    new RegExp(`${backgroundColorRegex.source}${colorRegex.source}`, "gi"),
    `background: ${backgroundColor};`
  );

  // Adjust link buttons background and text colors
  adjustedTemplate = adjustedTemplate.replace(
    new RegExp(`<a([^>]*?)${backgroundColorRegex.source}${colorRegex.source}(.*?)>`, "gi"),
    substring => {
      let templateWithAdjustedLinkButtons = substring;

      templateWithAdjustedLinkButtons = replaceColorInTemplate(
        templateWithAdjustedLinkButtons,
        new RegExp(`${backgroundColorRegex.source}${colorRegex.source}`, "gi"),
        `background: ${withVar(DesignToken.ActionPrimaryDefault)}`
      );
      templateWithAdjustedLinkButtons = templateWithAdjustedLinkButtons.replace(mixBlendModeStyle, "");
      templateWithAdjustedLinkButtons = templateWithAdjustedLinkButtons.replace(
        new RegExp(`[^-]color:\\s*${colorRegex.source}`, "gi"),
        match => `${match[0]}color: ${withVar(DesignToken.ContentInvertPrimary)}`
      );

      return templateWithAdjustedLinkButtons;
    }
  );

  return adjustedTemplate;
};

enum MessageDisplayMode {
  Initial = "initial",
  Original = "original",
  Unstripped = "unstripped",
}

interface IMessageEventOptionsProps {
  items: IActionMenuOption[];
  author: IEventAuthor | null;
}

const MessageEventOptions: React.FC<IMessageEventOptionsProps> = ({ items, author }) => {
  return (
    <ActionMenu
      options={items}
      placement="bottom-end"
      triggerRenderer={
        <Button
          kind="plain"
          size="xcompact"
          icon={
            <Icon
              source={MoreHoriz}
              size="small"
              customColor={withVar(
                author?.type === "client" ? DesignToken.ContentBasicPrimary : DesignToken.ContentInvertPrimary
              )}
            />
          }
        />
      }
    />
  );
};

interface IMessageOriginalContentProps {
  eventId: string;
}

interface IMessageOriginalContentDispatchProps {
  fetchOriginalMessage: () => void;
}

interface IMessageOriginalContentStateProps {
  event?: IEvent;
}

const mapStateToProps = (
  state: IStoreState,
  ownProps: IMessageOriginalContentProps
): IMessageOriginalContentStateProps => ({
  event: selectEventById(state, ownProps.eventId),
});

const mapDispatchToProps = (
  dispatch: Dispatch,
  ownProps: IMessageOriginalContentProps
): IMessageOriginalContentDispatchProps => ({
  // @ts-expect-error TS(2345)
  fetchOriginalMessage: () => dispatch(TicketsViewActions.fetchOriginalMessage({ eventId: ownProps.eventId })),
});

const IframeBox = styled.div`
  iframe {
    width: 100%;
    overflow: auto;
  }
`;
const UnconnectedMessageOriginalContent: React.FC<
  IMessageOriginalContentProps & IMessageOriginalContentDispatchProps & IMessageOriginalContentStateProps
> = ({ fetchOriginalMessage, event }) => {
  const [originalMessage, setOriginalMessage] = useState<IEvent["originalMessage"] | null>(null);
  const isLoading = useSelector(getIsLoadingOriginalMessage);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    if (!event?.originalMessage && !originalMessage) {
      fetchOriginalMessage();
    }
    if (event?.originalMessage && !originalMessage) {
      // Saving the original message in the state to avoid fetching it again when the event is updated.
      setOriginalMessage(event.originalMessage);
    }
  }, [event, fetchOriginalMessage]);

  useEffect(() => {
    if (originalMessage) {
      const iframe = iframeRef.current;
      if (!iframe) return;

      iframe.addEventListener("load", () => {
        iframe.height = `${(iframe.contentDocument?.body.scrollHeight ?? 0) + 20}px`;
        Array.from(iframe.contentDocument?.querySelectorAll("a") ?? []).forEach(link => {
          link.setAttribute("target", "_blank noreferrer noopener");
        });
      });

      if (typeof iframe.srcdoc !== "undefined") {
        iframe.srcdoc = originalMessage?.html ? originalMessage?.html : `<pre>${originalMessage.text}</pre>`;
      } else {
        iframe.contentWindow?.document.open();
        iframe.contentWindow?.document.write(originalMessage?.html);
        iframe.contentWindow?.document.close();
      }
    }
  }, [originalMessage]);

  if (!event) {
    return null;
  }

  if (isLoading) {
    return <p>Loading original message…</p>;
  }

  return (
    <IframeBox>
      <iframe
        ref={iframeRef}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        frameBorder="0"
        allowFullScreen={false}
        scrolling="auto"
      />
    </IframeBox>
  );
};

const MessageOriginalContent = connect(mapStateToProps, mapDispatchToProps)(UnconnectedMessageOriginalContent);
interface IMessageContentProps {
  id: string;
  type: EventType;
  isPrivate: boolean;
  author: IEventAuthor | null;
  message: React.ReactElement;
  attachments: IAttachment[] | null;
  displayMode: MessageDisplayMode;
  ticketId: string;
  showInitial: () => void;
  stripped: boolean;
  showUnstripped: () => void;
  eventTooOldForStrippedContent: boolean;
}

const MessageContent: React.FC<IMessageContentProps> = ({
  id,
  type,
  message,
  attachments,
  displayMode,
  ticketId,
  showInitial,
  stripped,
  showUnstripped,
  eventTooOldForStrippedContent,
}) => {
  const isDarkMode = useSelector(selectIsDarkMode);
  const { invertColorsInDarkMode } = useSelector(selectLoggedInAgent).settings;

  const isPlainText = messageIsPlainText(message);
  const messageEmpty = messageIsEmpty(message);
  const messageOrAttachmentExists = !messageEmpty || eventContainsTicketFeedMessage(type) || attachments;

  switch (displayMode) {
    case MessageDisplayMode.Initial: {
      const plainTextMessageText = getPlainTextMessageText(message);
      const shouldConvertToDarkMode = isDarkMode && invertColorsInDarkMode;

      return (
        <>
          {!messageOrAttachmentExists && <EmptyMessage>This message has no content.</EmptyMessage>}
          {messageOrAttachmentExists && (
            <>
              {isPlainText && !messageEmpty && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: shouldConvertToDarkMode
                      ? adjustInlineStyleColorsToDarkMode(plainTextMessageText)
                      : plainTextMessageText,
                  }}
                  dir="auto"
                  css={shouldConvertToDarkMode && css({ mixBlendMode: "screen" })}
                />
              )}
              {!isPlainText && <MessageElementWrapper>{message}</MessageElementWrapper>}
              {stripped && (
                <ShowUnstrippedContentButton onClick={showUnstripped} disabled={eventTooOldForStrippedContent} />
              )}
              {attachments && (
                <TicketAttachments attachments={attachments} hasMessage={!messageEmpty} ticketId={ticketId} />
              )}
            </>
          )}
        </>
      );
    }
    case MessageDisplayMode.Original:
      return (
        <>
          <MessageOriginalContent eventId={id} />
          {attachments && (
            <TicketAttachments attachments={attachments} hasMessage={!messageEmpty} ticketId={ticketId} />
          )}
        </>
      );
    case MessageDisplayMode.Unstripped:
      return (
        <>
          <MessageUnstrippedContent id={id} hasAttachments={!!attachments} showInitial={showInitial} />
          {attachments && (
            <TicketAttachments attachments={attachments} hasMessage={!messageEmpty} ticketId={ticketId} />
          )}
        </>
      );
  }
};

export const TicketMessageEvent: React.FC<IProps> = ({
  id,
  author,
  agents,
  isPrivate,
  source,
  type,
  testId,
  date,
  message,
  attachments,
  isMobile = false,
  deliveryStatuses = [],
  ticketId,
  stripped,
  replySuggestionIDs,
}) => {
  const [displayMode, setDisplayMode] = React.useState<MessageDisplayMode>(MessageDisplayMode.Initial);

  const deliveryStatus = deliveryStatuses?.find(s => s.entityType === Author.Customer);
  const supportsDeliveryStatus = eventSupportsDeliveryStatus(type, author);
  const authorEmail = getAuthorEmail(agents, author);
  const fromAddress = source?.from;
  const replyToAddress = author?.email;
  const shouldDisplayAddressLabel =
    fromAddress && replyToAddress && fromAddress !== replyToAddress && author.type === Author.Customer;
  const isSummary = type === EventType.Summary;
  const shouldDisplayPrivateLabel = isSummary || isPrivate;
  const eventTooOldForStrippedContent = isBefore(date, subDays(new Date(), 45));
  const wasReplySuggested = !!replySuggestionIDs;

  const showBottomMeta = (supportsDeliveryStatus && deliveryStatus) || wasReplySuggested;

  const handleShowOriginal = (): void => {
    setDisplayMode(MessageDisplayMode.Original);
  };

  const handleShowInitial = (): void => {
    setDisplayMode(MessageDisplayMode.Initial);
  };

  const handleShowUnstripped = (): void => {
    setDisplayMode(MessageDisplayMode.Unstripped);
  };

  const eventOptions = (displayMode: MessageDisplayMode, source: IEvent["source"]): IActionMenuOption[] =>
    !source || source.detailedSource !== DetailedSource.Email
      ? []
      : [
          ...(displayMode === MessageDisplayMode.Initial
            ? [
                {
                  key: "show-original",
                  element: <ActionMenuItem>Show original message</ActionMenuItem>,
                  onClick: handleShowOriginal,
                },
              ]
            : []),
          ...(displayMode === MessageDisplayMode.Original || displayMode === MessageDisplayMode.Unstripped
            ? [
                {
                  key: "show-initial",
                  element: <ActionMenuItem>Show initial message</ActionMenuItem>,
                  onClick: handleShowInitial,
                },
              ]
            : []),
        ];

  return (
    <Wrapper data-testid={testId} isMobile={isMobile}>
      <StyledMetaTop author={author} type={type} isPrivate={shouldDisplayPrivateLabel}>
        <StyledAuthor size="sm" bold {...(authorEmail ? { title: authorEmail } : {})}>
          {shouldDisplayPrivateLabel && <StyledIcon source={Spy} size="small" />}
          {isSummary ? (
            <Tag size="small" kind="gradient02">
              <DSText size="sm" bold>
                Ticket summary
              </DSText>
            </Tag>
          ) : (
            getAuthorName(agents, author)
          )}
          {shouldDisplayPrivateLabel && <StyledPrivateMessageInfo>- Private note</StyledPrivateMessageInfo>}
        </StyledAuthor>
        {isSameDay(date, new Date()) ? (
          <Tooltip
            kind="invert"
            fullSpaceContent
            transitionDelay={400}
            css={css({ padding: "4px 6px" })}
            placement="top-start"
            triggerRenderer={() => getFormattedEventDate(date)}
          >
            <DSText size="xs">{getEventDate(date)}</DSText>
          </Tooltip>
        ) : (
          <DSText size="xs">{getFormattedEventDate(date)}</DSText>
        )}
        {eventOptions(displayMode, source).length > 0 && (
          <MessageEventOptions items={eventOptions(displayMode, source)} author={author} />
        )}
      </StyledMetaTop>
      {shouldDisplayAddressLabel && (
        <Flex padding="8px 12px" gap="4px" column border={`1px solid var(${DesignToken.BorderBasicSecondary})`}>
          <DSText size="xs" customColor={withVar(DesignToken.ContentBasicSecondary)} css={textEllipsisStyles}>
            From: <span css={{ color: withVar(DesignToken.ContentBasicPrimary) }}>{fromAddress}</span>
          </DSText>
          <DSText size="xs" customColor={withVar(DesignToken.ContentBasicSecondary)} css={textEllipsisStyles}>
            Reply to: <span css={{ color: withVar(DesignToken.ContentBasicPrimary) }}>{replyToAddress}</span>
          </DSText>
        </Flex>
      )}
      <StyledBubble author={author} type={type} isPrivate={isPrivate}>
        <MessageContent
          id={id}
          author={author}
          type={type}
          isPrivate={isPrivate}
          attachments={attachments}
          message={message}
          displayMode={displayMode}
          ticketId={ticketId}
          showInitial={handleShowInitial}
          stripped={stripped ?? false}
          showUnstripped={handleShowUnstripped}
          eventTooOldForStrippedContent={eventTooOldForStrippedContent}
        />
      </StyledBubble>
      {showBottomMeta && (
        <StyledMetaBottom>
          {deliveryStatus && <TicketDeliveryStatus status={deliveryStatus.status} />}
          {wasReplySuggested && <ReplySuggestionIndicator />}
        </StyledMetaBottom>
      )}
    </Wrapper>
  );
};

const ReplySuggestionIndicator: React.FC = () => {
  return (
    <Flex gap={withVar(SpacingToken.Spacing1)} inline alignItems="center">
      <Icon source={OneColored} size="small" />
      <DSText customColor={withVar(DesignToken.ContentBasicSecondary)} size="sm">
        Reply suggested by Copilot
      </DSText>
    </Flex>
  );
};
